import React from 'react';
import { Link } from '@tanstack/react-router';
import { MapPin, Calendar, Clock, Navigation } from 'lucide-react';
import { Badge, Image as ImageComponent } from '@city-sage/aurum-avis-labs-components';
import { cn } from '@city-sage/aurum-avis-labs-components';
import { Stop } from '@city-sage/core-client';
import { MarkdownRenderer } from '../MarkdownRenderer';

interface StopSearchCardProps {
  stop: Stop;
  searchQuery: string;
  className?: string;
  onClose?: () => void;
}

export function StopSearchCard({ 
  stop, 
  searchQuery, 
  className,
  onClose
}: StopSearchCardProps) {
  const highlightText = (text: string, query: string) => {
    if (!query) return text;
    
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 text-black font-medium">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const getStopImage = () => {
    // Try to get image from stop or place
    if (stop.imageUrl) {
      return stop.imageUrl;
    }
    
    if (stop.place?.imageUrl) {
      return stop.place.imageUrl;
    }
    
    return undefined;
  };

  const getStopImageEntity = () => {
    // Only return image entity if using stop's image (not place's image)
    if (stop.imageUrl) {
      return stop.image || undefined;
    }
    
    return undefined;
  };

  const getLocationText = () => {
    // Use address from stop
    if (stop.place.address) {
      const { city, state, countryName } = stop.place.address;
      const parts = [city, state, countryName].filter(Boolean);
      return parts.join(', ');
    }
    
    // Fallback to place name
    if (stop.place?.name) {
      return stop.place.name;
    }
    
    return 'Unknown location';
  };

  const getPlaceCategory = () => {
    if (stop.place?.featureType) {
      return stop.place.featureType.replace('_', ' ').toLowerCase();
    }
    
    return null;
  };

  const getTimeInfo = () => {
    if (stop.startTime) {
      // startTime is already in HH:MM format from the entity
      const [hours, minutes] = stop.startTime.split(':');
      const time = new Date();
      time.setHours(parseInt(hours), parseInt(minutes));
      return time.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
    return null;
  };

  return (
    <Link to="/itineraries/$id/stops/$stopId" params={{ id: stop.itineraryId, stopId: stop.id }} onClick={onClose}>
      <div className={cn(
        "bg-white px-6 py-4 hover:bg-gray-50 transition-colors duration-200 border-b border-gray-100 last:border-b-0",
        className
      )}>
        <div className="flex gap-4">
          {/* Image */}
          <div className="relative flex-shrink-0">
            <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
              {getStopImage() ? (
                <ImageComponent
                  src={getStopImage()}
                  image={getStopImageEntity()}
                  alt={stop.title || stop.place?.name}
                  className="w-full h-full"
                  imageClassName="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                <MapPin className="h-6 w-6 text-blue-500" />
              </div>
              )}
            </div>
        
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-2">
            {/* Title */}
            <div>
              <h3 className="font-semibold text-base text-gray-900 truncate">
                {highlightText(stop.title, searchQuery)}
              </h3>
              {stop.description && (
                <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                  {highlightText(stop.description, searchQuery)}
                </p>
              )}
              {stop.highlight && (
                <div className="text-sm text-blue-600 line-clamp-1 mt-1 font-medium">
                  <MarkdownRenderer content={stop.highlight} className="text-sm text-blue-600 font-medium" />
                </div>
              )}
            </div>

            {/* Location & Category */}
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-1 truncate">
                <Navigation className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{getLocationText()}</span>
              </div>
              
              {getPlaceCategory() && (
                <Badge variant="outline" className="text-xs">
                  {getPlaceCategory()}
                </Badge>
              )}
            </div>

            {/* Time & Duration Info */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 text-xs text-gray-500">
                {getTimeInfo() && (
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>{getTimeInfo()}</span>
                  </div>
                )}
                
                {stop.durationMinutes && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <span>{stop.durationMinutes}min</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
} 